import {
  Link,
  Progress,
  ScrollShadow,
  Spinner,
} from '@heroui/react'
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { TopBar } from 'components/Layout/TopBar'
import { TopBarContent } from 'components/Layout/TopBarProvider'
import GradientLoadingAnimation from 'components/Reusables/GradientLoadingAnimation'
import { Button } from 'components/ui/Button'
import useCallLogsTable from 'hooks/useCallLogsTable'
import { useCallLogsPagination } from 'hooks/useCallsPagination'
import { Circle, List, PhoneCall, Voicemail, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { fetchCallLogs, fetchCallLogsCount, getNextPageParam, initialPageParam } from 'utils/funcs/call-logs/callLogsQueries'
import { convertParamsToFilters } from 'utils/funcs/call-logs/filterFns'
import ActiveCallsView from './ActiveCalls/ActiveCallsView'
import CallDetailsSlideOut from './CallDetailsSlideOut'
import CallLogsTable from './CallLogsTable'
import ExportCalls from './ExportCalls'
import FilterBar from './Filters/FilterBar'
import ResizableDrawer from './ResizeableDrawer'

function CallLogs() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { callId } = useParams()
  const navigate = useNavigate()
  const tableContainerRef = useRef(null)

  const { take, TAKE_EXTRA_ROWS, ROW_HEIGHT } = useCallLogsPagination()
  const sortBy = searchParams.get('sortBy') || 'created_at'
  const sortDir = searchParams.get('sortDir') || 'desc'
  const mode = searchParams.get('mode') || 'AND'
  const view = searchParams.get('view') || 'all'

  const [liveCallId, setLiveCallId] = useState(null)

  const filters = useMemo(() => convertParamsToFilters(searchParams), [searchParams])
  const queryKey = ['callLogs', take, filters, mode, sortBy, sortDir]

  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: fetchCallLogs,
    initialPageParam,
    getNextPageParam,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 3, // 5 minutes
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: 'Error fetching call logs',
    },
  })

  const { data: totalCount } = useQuery({
    queryKey: ['callLogsCount', filters, mode],
    queryFn: fetchCallLogsCount,
    staleTime: 1000 * 60 * 3, // 5 minutes
  })

  // Flatten the data array for the table
  const flatData = useMemo(
    () => data?.pages?.flatMap(page => page.calls) ?? [],
    [data],
  )

  const table = useCallLogsTable({ totalCount, data: flatData })

  // Function to fetch more data when scrolling to bottom
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement

        if (
          scrollHeight - scrollTop - clientHeight < TAKE_EXTRA_ROWS * ROW_HEIGHT
          && !isFetchingNextPage
          && hasNextPage
        ) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage],
  )

  // Check if we need to fetch more data on mount or after a fetch
  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current)
  }, [fetchMoreOnBottomReached])

  useEffect(() => {
    // Get row information once
    const getRowInfo = () => {
      if (!callId || !flatData?.length || !table)
        return null

      const rowModel = table.getRowModel()
      const selectedRow = table.getRow(callId)
      const currentIndex = rowModel.rows.findIndex(row => row.id === callId)
      const rowElement = tableContainerRef.current?.querySelector(`tr[data-row-id="${callId}"]`)
        || tableContainerRef.current?.querySelector(`tbody tr:nth-child(${selectedRow?.index + 1})`)

      return {
        rowModel,
        selectedRow,
        currentIndex,
        rowElement,
      }
    }

    const handleKeyDown = (e) => {
      const activeElement = document.activeElement
      const isEditableElement
        = activeElement.tagName === 'INPUT'
          || activeElement.tagName === 'TEXTAREA'
          || activeElement.contentEditable === 'true'
          || activeElement.closest('[contenteditable="true"]')
          || activeElement.closest('.ignore-audio-player')

      if (isEditableElement || !flatData?.length || !['ArrowDown', 'ArrowUp', 'j', 'k', 'Enter'].includes(e.key)) {
        return
      }

      const rowInfo = getRowInfo()
      if (!rowInfo)
        return

      const { rowModel, selectedRow, currentIndex } = rowInfo
      let nextIndex = currentIndex

      switch (e.key) {
        case 'ArrowDown':
        case 'k':
          e.preventDefault()
          nextIndex += 1
          break
        case 'ArrowUp':
        case 'j':
          e.preventDefault()
          if (selectedRow) {
            nextIndex -= 1
          }
          break
        case 'Enter':
          e.preventDefault()
          if (selectedRow?.subRows?.length) {
            return selectedRow.getToggleExpandedHandler()()
          }
          break
      }

      const nextRow = rowModel.rows[nextIndex]
      if (nextRow) {
        table.setRowSelection(() => ({ [nextRow.id]: true }))
      }
    }

    // Scroll to selected row
    const scrollToSelectedRow = () => {
      if (!tableContainerRef.current || !callId)
        return

      const rowInfo = getRowInfo()
      if (!rowInfo || !rowInfo.rowElement)
        return

      const { rowElement } = rowInfo
      const containerRect = tableContainerRef.current.getBoundingClientRect()
      const rowRect = rowElement.getBoundingClientRect()

      // Check if the row is fully visible in the viewport
      const isRowVisible
        = rowRect.top >= containerRect.top
          && rowRect.bottom <= containerRect.bottom

      // If not visible, scroll it into view
      if (!isRowVisible) {
        // Determine scroll direction
        if (rowRect.top < containerRect.top) {
          // Scroll up if row is above viewport
          rowElement.scrollIntoView({ block: 'start', behavior: 'smooth' })
        }
        else if (rowRect.bottom > containerRect.bottom) {
          // Scroll down if row is below viewport
          rowElement.scrollIntoView({ block: 'end', behavior: 'smooth' })
        }
      }
    }

    // Call it once when callId changes, with a small delay
    const timeoutId = setTimeout(scrollToSelectedRow, 10)

    window.addEventListener('keydown', handleKeyDown)

    // Clean up event listener and timeout
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      clearTimeout(timeoutId)
    }
  }, [callId, flatData, table])

  const tabItems = [
    { value: 'all', label: 'Completed', icon: List },
    { value: 'active', label: 'Active', icon: Circle },
  ]

  return (
    <div className="flex h-full flex-col">
      <TopBarContent>
        <TopBar.Title>Call Logs</TopBar.Title>
        <TopBar.Tabs
          selectedKey={view}
          onSelectionChange={(value) => {
            searchParams.set('view', value)
            setSearchParams(searchParams)
          }}
        >
          <TopBar.Tabs.List>
            {tabItems.map(tab => (
              <TopBar.Tabs.Tab key={tab.value} id={tab.value} icon={tab.icon}>
                {tab.label}
              </TopBar.Tabs.Tab>
            ))}
          </TopBar.Tabs.List>
        </TopBar.Tabs>
        <TopBar.Actions>
          <ExportCalls activeFilters={filters} />
          {isFetching && !isFetchingNextPage && (
            <Progress
              isIndeterminate
              aria-label="Loading..."
              color="default"
              classNames={{ base: 'h-1 absolute bottom-0 left-0 h-full', indicator: 'duration-1000!' }}
              size="sm"
            />
          )}
        </TopBar.Actions>
      </TopBarContent>

      {view === 'active'
        ? (
            <ActiveCallsView
              handleViewDetails={() => navigate(`/dashboard/call-logs/${liveCallId}`)}
              setSelectedCallId={setLiveCallId}
              selectedCallId={liveCallId}
            />
          )
        : isLoading
          ? (
              <GradientLoadingAnimation
                message="Loading Call Logs"
                variant="roseGarden"
                rounded="full"
                gray
              />
            )
          : (
              <div className="flex h-full max-h-full flex-col">
                <FilterBar appliedFilters={filters} appliedMode={mode} />

                {/* Table */}
                {!flatData?.length
                  ? (
                      <div className={`
                        mt-40 flex h-full w-full flex-col items-center
                      `}
                      >
                        <Voicemail size={64} className="text-gray-800" />
                        <h1 className={`
                          mb-6 text-3xl font-semibold tracking-tighter
                        `}
                        >
                          No calls found.
                        </h1>
                        {filters?.length > 0
                          ? (
                              <Button
                                variant="bordered"
                                color="default"
                                radius="sm"
                                size="lg"
                                onPress={() => {
                                  setSearchParams({ take })
                                }}
                              >
                                <X size={16} />
                                Clear Filters
                              </Button>
                            )
                          : (
                              <Button
                                variant="solid"
                                color="primary"
                                radius="sm"
                                size="lg"
                                as={Link}
                                href="/dashboard/send-call"
                              >
                                <PhoneCall size={16} />
                                Make your first call
                              </Button>
                            )}
                      </div>
                    )
                  : (
                      <ScrollShadow
                        ref={tableContainerRef}
                        className="flex-1 px-4"
                        onScroll={e => fetchMoreOnBottomReached(e.target)}
                        orientation="vertical"
                        visibility="bottom"
                        isEnabled={hasNextPage}
                      >
                        <CallLogsTable table={table} isRefreshing={isFetching && !isFetchingNextPage} />
                        {isFetchingNextPage && (
                          <div className="flex items-center justify-center py-4">
                            <Spinner
                              variant="spinner"
                              aria-label="Loading more call logs..."
                            />
                          </div>
                        )}
                      </ScrollShadow>
                    )}
                {!!callId && (
                  <ResizableDrawer>
                    <CallDetailsSlideOut
                      callId={callId}
                      queryKey={queryKey}
                      onClose={() => table.setRowSelection(null)}
                    />
                  </ResizableDrawer>
                )}
              </div>
            )}
    </div>
  )
}

export default CallLogs
